import { Route, RouteConfig } from 'vue-router';
import { addFunds } from '@src/store/modules/add-funds';
import { AddFundsUpgradeRoute } from 'types/add-funds';
import { addFundsUpgradeRouteOrder } from '@src/constants';
import { app } from '@store/modules/app';
import { investmentEntity } from '@src/store/modules/investment-entity';
import { upgrade } from '@store/modules/upgrade';

const getFlowStepIndex = (name: AddFundsUpgradeRoute): number => {
	return addFundsUpgradeRouteOrder.indexOf(name) + 1;
};

const stepConstructor = (route: Route): number => {
	return getFlowStepIndex(route.name as AddFundsUpgradeRoute);
};

const upgradeRequiresCorePlanSelection = (): boolean => {
	const currentLevelBelowCore =
		investmentEntity.currentServiceLevel.name === 'STARTER' ||
		investmentEntity.currentServiceLevel.name === 'BASIC' ||
		investmentEntity.currentRiaPlanInvestmentStrategy === 'BASIC_PORTFOLIO';
	const upgradeCoreOrHigher =
		upgrade.upgradeLevelName === 'CORE' ||
		upgrade.upgradeLevelName === 'ADVANCED' ||
		upgrade.upgradeLevelName === 'PREMIUM';
	return currentLevelBelowCore && upgradeCoreOrHigher;
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const upgradeRoutes: Array<RouteConfig> = [
	{
		path: `/account/upgrade`,
		name: 'upgrade',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import(/* webpackChunkName: "upgrade" */ '@views/account/upgrade/upgrade.vue'),
		beforeEnter: async (to, from, next) => {
			if (!upgrade.dataLoaded || addFunds.orderComplete) {
				await upgrade.fetchAndStoreServiceLevelUpgradeData();
			}

			next();
		},
		redirect: { name: 'upgrade-landing' },
		children: [
			{
				path: 'start',
				name: 'upgrade-landing',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (!upgrade.hasAvailableNextLevel) {
						next({ name: 'account-overview', replace: true });
						app.ADD_TOAST({
							type: 'error',
							message: 'The current account is not eligible for an upgrade at this time.'
						});
					} else {
						addFunds.resetInvestTabAddFunds();
						next();
					}
				},
				// Add route guards to below pages enforcing visiting start first for UPGRADE flow (not INVEST-UPGRADE)
				component: () =>
					import(/* webpackChunkName: "upgrade-start" */ '@views/account/upgrade/upgrade-start.vue')
			},
			{
				path: 'select-core-plan',
				name: 'upgrade-core-plans',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (investmentEntity.isIraEntity) {
						next({ name: 'account-overview', replace: true });
					} else if (!upgrade.dataLoaded || !upgradeRequiresCorePlanSelection()) {
						next({ name: 'upgrade-landing', replace: true });
					} else if (!upgrade.aboveUpgradeThreshold && !addFunds.investUpgrade) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(/* webpackChunkName: "upgrade-core-plans" */ '@views/account/upgrade/upgrade-core-plans.vue')
			},
			{
				path: 'accreditation-information',
				name: 'upgrade-accreditation',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (!upgrade.premiumShouldSeeAccreditationPage) {
						next({ name: 'account-overview', replace: true });
					} else if (!upgrade.dataLoaded || upgrade.upgradeLevelName !== 'PREMIUM') {
						next({ name: 'upgrade-landing', replace: true });
					} else if (!upgrade.aboveUpgradeThreshold && !addFunds.investUpgrade) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "upgrade-accreditation" */ '@views/account/upgrade/upgrade-accreditation.vue'
					)
			},
			{
				path: 'view-allocation',
				name: 'upgrade-view-allocation',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					await upgrade.fetchAndStoreCurrentlySelectedRiaPlan();
					if (!upgrade.newRiaPlan?.id || !upgrade.upgradeLevelName || !addFunds.amount) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else if (upgrade.upgradeLevelName === 'PREMIUM' && !addFunds.investTabAddFunds) {
						next({ name: 'account-overview', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "upgrade-view-allocation" */ '@views/account/upgrade/upgrade-view-allocation.vue'
					)
			},
			{
				path: 'success',
				name: 'upgrade-success',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () =>
					import(/* webpackChunkName: "upgrade-success" */ '@views/account/upgrade/upgrade-success.vue')
			}
		]
	}
];

export default upgradeRoutes;
